/**
 * PatientSearchPanel — Searchable patient lookup with MRN, name, or ID.
 *
 * Integrates with the patient search API to find existing patients.
 * On selection, populates the PatientContextPanel with the patient's data
 * and links the patient to the current session.
 *
 * PHI: All patient data is encrypted at rest. Search is performed server-side.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import patientService, {
  PatientSearchResult,
  PatientSearchParams,
  PatientResponse,
} from '../services/patientService';

interface PatientSearchPanelProps {
  onPatientSelected: (patient: PatientResponse | null) => void;
  disabled?: boolean;
  sessionId?: string | null;
}

const PatientSearchPanel: React.FC<PatientSearchPanelProps> = ({
  onPatientSelected,
  disabled = false,
  sessionId,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<PatientSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PatientSearchResult | null>(null);
  const [loadingPatient, setLoadingPatient] = useState(false);

  // Load patient linked to current session on mount
  useEffect(() => {
    if (!sessionId) {
      setSelectedPatient(null);
      setInputValue('');
      return;
    }
    let active = true;

    (async () => {
      try {
        const patient = await patientService.getSessionPatient(sessionId);
        if (!active || !patient) return;
        // Convert PatientResponse to PatientSearchResult for display
        const searchResult: PatientSearchResult = {
          patient_id: patient.id,
          display_name: [patient.first_name, patient.last_name].filter(Boolean).join(' ') || patient.mrn || 'Unknown',
          dob: patient.dob || null,
          mrn: patient.mrn || null,
          gender: patient.gender || null,
          report_count: 0,
        };
        setSelectedPatient(searchResult);
        console.log('PATIENT_LOADED_FROM_SESSION | session_id=%s | patient_id=%s', sessionId, patient.id);
        onPatientSelected(patient);
      } catch (err: unknown) {
        // 404 = no patient linked, which is fine
        const axiosErr = err as { response?: { status?: number } };
        if (axiosErr.response?.status !== 404) {
          console.warn('Failed to load session patient:', err);
        }
      }
    })();

    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Debounced search
  useEffect(() => {
    if (!inputValue || inputValue.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const params: PatientSearchParams = {};
        // Detect search type: MRN (numeric), or name (alphabetic)
        if (/^\d+$/.test(inputValue)) {
          params.mrn = inputValue;
        } else {
          params.name = inputValue;
        }
        params.limit = 15;

        const response = await patientService.searchPatients(params);
        setSearchResults(response.patients);
      } catch (err) {
        console.error('Patient search failed:', err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);

    return () => {
      clearTimeout(timer);
      setSearching(false);
    };
  }, [inputValue]);

  const handleSelect = useCallback(
    async (_event: React.SyntheticEvent, value: PatientSearchResult | null) => {
      setSelectedPatient(value);
      if (!value) {
        onPatientSelected(null);
        return;
      }

      setLoadingPatient(true);
      try {
        const fullPatient = await patientService.getPatient(value.patient_id);
        console.log(
          'PATIENT_SELECTED | patient_id=%s | mrn=%s | name=%s | session_id=%s',
          value.patient_id, value.mrn, value.display_name, sessionId,
        );
        onPatientSelected(fullPatient);
      } catch (err) {
        console.error('Failed to load patient details:', err);
        onPatientSelected(null);
      } finally {
        setLoadingPatient(false);
      }
    },
    [onPatientSelected, sessionId]
  );

  const handleClear = useCallback(() => {
    setSelectedPatient(null);
    setInputValue('');
    onPatientSelected(null);
  }, [onPatientSelected]);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mb: 1.5,
        backgroundColor: selectedPatient ? 'primary.50' : 'background.paper',
        borderColor: selectedPatient ? 'primary.light' : 'divider',
        borderRadius: 2,
        transition: 'all 0.3s ease',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <PersonSearchIcon sx={{ fontSize: 18, color: selectedPatient ? 'primary.main' : 'text.secondary', mr: 0.5 }} />
        <Typography variant="subtitle2" color={selectedPatient ? 'primary.dark' : 'text.secondary'} fontWeight={600}>
          Patient Search
        </Typography>
        {selectedPatient && (
          <Chip
            icon={<LockIcon sx={{ fontSize: 10 }} aria-hidden="true" />}
            label="PHI Encrypted"
            size="small"
            color="success"
            variant="outlined"
            sx={{ ml: 1, fontSize: '0.6rem', height: 20, fontWeight: 500 }}
          />
        )}
        {loadingPatient && (
          <CircularProgress size={14} sx={{ ml: 1 }} />
        )}
        {selectedPatient && !disabled && (
          <Tooltip title="Clear patient">
            <IconButton size="small" onClick={handleClear} sx={{ ml: 'auto', p: 0.25 }}>
              <ClearIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Search input — shown when no patient selected */}
      {!selectedPatient && (
        <Autocomplete
          size="small"
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          value={selectedPatient}
          onChange={handleSelect}
          inputValue={inputValue}
          onInputChange={(_e, val) => setInputValue(val)}
          options={searchResults}
          loading={searching}
          disabled={disabled}
          getOptionLabel={(option) => option.display_name}
          isOptionEqualToValue={(option, value) =>
            option.patient_id === value.patient_id
          }
          renderOption={(props, option) => (
            <li {...props} key={option.patient_id}>
              <Box sx={{ py: 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" fontWeight={600}>
                    {option.display_name}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {option.mrn ? `MRN: ${option.mrn}` : ''}
                  {option.dob ? ` · DOB: ${option.dob}` : ''}
                  {option.gender ? ` · ${option.gender}` : ''}
                  {option.report_count > 0 ? ` · ${option.report_count} reports` : ''}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search by MRN, name, or patient ID…"
              InputProps={{
                ...params.InputProps,
                startAdornment: <PersonSearchIcon sx={{ fontSize: 18, color: 'text.disabled', mr: 0.5 }} />,
                endAdornment: (
                  <>
                    {searching ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={
            inputValue.length < 2
              ? 'Type at least 2 characters to search'
              : 'No patients found'
          }
        />
      )}

      {/* Selected patient details */}
      {selectedPatient && (
        <Box sx={{ mt: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <PersonIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
              {selectedPatient.display_name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {selectedPatient.mrn && (
              <Chip size="small" label={`MRN: ${selectedPatient.mrn}`} variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            )}
            {selectedPatient.dob && (
              <Chip size="small" label={`DOB: ${selectedPatient.dob}`} variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            )}
            {selectedPatient.gender && (
              <Chip size="small" label={selectedPatient.gender === 'M' ? 'Male' : selectedPatient.gender === 'F' ? 'Female' : selectedPatient.gender} variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            )}
            {selectedPatient.report_count > 0 && (
              <Chip size="small" label={`${selectedPatient.report_count} prior reports`} color="info" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default PatientSearchPanel;
